import React, { useCallback, useEffect, useMemo } from "react";
import { editorHeader } from "./_styles/header";
import { editorSection } from "./_styles/section";
import { H4, Text } from "@ui/Text";
import t from "../../../../../../../i18n";
import { useCurrentEditorBlock } from "../../../../hook/context/gui";
import type { BlockPresetModelT } from "../../../../schema/block/preset";
import { BlockPosition } from "./BlockPosition";
import ReactSelect from "@ui/ReactSelect";
import { useCategories } from "../../../../../../../api/useCategories";
import { buildOptions } from "@ui/Filter";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import type { MultiValue } from "react-select";
import createGlobalState from "@ui/lib/createGlobalState";
import * as io from "io-ts";
import { css } from "@linaria/core";
import { useDashboardEditorContext } from "../../../../hook/context/editor";
import cx from "classnames";
import { colors, fonts } from "@ui/Theme";

const usePriorityFaultEventsSystemReminder = createGlobalState({
  storage: "session",
  key: "system-reminder",
  type: io.array(io.string),
  defaultValue: [],
  scoped: true,
});

export function PriorityFaultEventsEditor() {
  const editor = useDashboardEditorContext();
  const block = useCurrentEditorBlock<BlockPresetModelT>("PRESET");
  const systems = useCategories("SYSTEM");

  const [systemReminder, setSystemReminder] = usePriorityFaultEventsSystemReminder({
    scope: block?.id ?? "priority-fault-events",
  });
  const blockData = block?.data;
  const isPriorityFaultEvents = blockData?.preset === "PRIORITY_FAULT_EVENTS";
  const systemIds = isPriorityFaultEvents ? blockData.systemIds : systemReminder;

  useEffect(() => {
    if (!block || !isPriorityFaultEvents) return;
    if (!blockData.systemIds) {
      setSystemReminder([]);
      void editor?.updateBlock({
        ...block,
        data: {
          ...blockData,
          systemIds: [],
        },
      });
      return;
    }

    setSystemReminder(blockData.systemIds);
  }, [block, blockData, editor, isPriorityFaultEvents, setSystemReminder]);
  const systemOptions = useMemo(
    () => buildOptions(systems.map((systemId: string) => ({ label: systemId, value: systemId }))),
    [systems],
  );
  const selectedSystemOptions = useMemo(
    () =>
      systemIds.map(
        (systemId: string) =>
          systemOptions.find(option => option.value === systemId) ?? {
            label: systemId,
            value: systemId,
          },
      ),
    [systemIds, systemOptions],
  );

  const handleSystemsChange = useCallback(
    (value: MultiValue<BaseOption>) => {
      if (!block || !isPriorityFaultEvents) return;
      const nextSystemIds = value
        .map(option => option.value)
        .filter((systemId): systemId is string => Boolean(systemId));

      setSystemReminder(nextSystemIds);
      void editor?.updateBlock({
        ...block,
        data: {
          ...blockData,
          systemIds: nextSystemIds,
        },
      });
    },
    [block, editor, isPriorityFaultEvents, setSystemReminder, blockData],
  );

  if (!block || !isPriorityFaultEvents) return null;

  return (
    <div>
      <header className={cx(editorHeader, editorSection)}>
        <H4>{t("project.dashboard.editor.priority_events.title")}</H4>
      </header>
      <section className={editorSection}>
        <BlockPosition block={block} />
      </section>
      <section className={editorSection}>
        <div className={field}>
          <Text className={label}>{t("project.fault_events.table.live.metadata.system")}</Text>
          <ReactSelect
            isMulti
            menuPortalTarget={window.document.body}
            placeholder={t("common.select")}
            options={systemOptions}
            value={selectedSystemOptions}
            onChange={handleSystemsChange}
          />
        </div>
      </section>
    </div>
  );
}

const field = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const label = css`
  color: ${colors.carbon800};
  font: ${fonts.body1SemiBold};
`;
